// src/components/resources/RichTextRenderer.js
// Premium rich text renderer for Contentful content

import React, { useMemo } from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import './styles/rich-text.css';

/**
 * RichTextRenderer - Renders Contentful Rich Text with premium styling
 *
 * @param {object} content - Rich text content { json, links }
 * @param {string} className - Additional class name
 */
const RichTextRenderer = ({ content, className = '' }) => {
  // Build asset lookup map from links
  const assetMap = useMemo(() => {
    const map = new Map();
    content?.links?.assets?.block?.forEach((asset) => {
      if (asset?.sys?.id) {
        map.set(asset.sys.id, asset);
      }
    });
    return map;
  }, [content?.links?.assets?.block]);

  // Build entry lookup map from links
  const entryMap = useMemo(() => {
    const map = new Map();
    content?.links?.entries?.block?.forEach((entry) => {
      if (entry?.sys?.id) {
        map.set(entry.sys.id, entry);
      }
    });
    return map;
  }, [content?.links?.entries?.block]);

  // Renderer options
  const options = {
    renderMark: {
      [MARKS.BOLD]: (text) => <strong className="rich-text__bold">{text}</strong>,
      [MARKS.ITALIC]: (text) => <em className="rich-text__italic">{text}</em>,
      [MARKS.UNDERLINE]: (text) => <u className="rich-text__underline">{text}</u>,
      [MARKS.CODE]: (text) => <code className="rich-text__inline-code">{text}</code>,
    },

    renderNode: {
      // Headings
      [BLOCKS.HEADING_1]: (node, children) => (
        <h1 className="rich-text__h1">{children}</h1>
      ),
      [BLOCKS.HEADING_2]: (node, children) => (
        <h2 className="rich-text__h2">{children}</h2>
      ),
      [BLOCKS.HEADING_3]: (node, children) => (
        <h3 className="rich-text__h3">{children}</h3>
      ),
      [BLOCKS.HEADING_4]: (node, children) => (
        <h4 className="rich-text__h4">{children}</h4>
      ),
      [BLOCKS.HEADING_5]: (node, children) => (
        <h5 className="rich-text__h5">{children}</h5>
      ),
      [BLOCKS.HEADING_6]: (node, children) => (
        <h6 className="rich-text__h6">{children}</h6>
      ),

      // Paragraphs
      [BLOCKS.PARAGRAPH]: (node, children) => {
        // Check if paragraph only contains whitespace
        const hasContent = node.content.some(
          (item) => item.value?.trim() || item.nodeType !== 'text'
        );
        if (!hasContent) return null;
        return <p className="rich-text__paragraph">{children}</p>;
      },

      // Lists
      [BLOCKS.UL_LIST]: (node, children) => (
        <ul className="rich-text__list rich-text__list--unordered">{children}</ul>
      ),
      [BLOCKS.OL_LIST]: (node, children) => (
        <ol className="rich-text__list rich-text__list--ordered">{children}</ol>
      ),
      [BLOCKS.LIST_ITEM]: (node, children) => (
        <li className="rich-text__list-item">{children}</li>
      ),

      // Quote
      [BLOCKS.QUOTE]: (node, children) => (
        <blockquote className="rich-text__quote">
          <div className="rich-text__quote-border" />
          <div className="rich-text__quote-content">{children}</div>
        </blockquote>
      ),

      // Horizontal rule
      [BLOCKS.HR]: () => <hr className="rich-text__hr" />,

      // Embedded Asset (images)
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const assetId = node.data?.target?.sys?.id;
        const asset = assetMap.get(assetId);

        if (!asset) return null;

        // Check if it's an image
        const isImage = asset.contentType?.startsWith('image/');
        if (!isImage) {
          // Handle non-image assets (like PDFs)
          return (
            <a
              href={asset.url}
              className="rich-text__file-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              {asset.title || asset.url}
            </a>
          );
        }

        // Calculate aspect ratio class
        const aspectRatio =
          asset.width && asset.height ? asset.width / asset.height : 16 / 9;

        let aspectClass = 'rich-text__figure--standard';
        if (aspectRatio > 1.6) {
          aspectClass = 'rich-text__figure--wide';
        } else if (aspectRatio < 0.8) {
          aspectClass = 'rich-text__figure--tall';
        } else if (aspectRatio > 0.9 && aspectRatio < 1.1) {
          aspectClass = 'rich-text__figure--square';
        }

        // Generate responsive image URL
        const imageUrl = `${asset.url}?w=1200&q=80&fm=webp`;

        return (
          <figure className={`rich-text__figure ${aspectClass}`}>
            <img
              src={imageUrl}
              alt={asset.description || asset.title || ''}
              className="rich-text__image"
              loading="lazy"
              width={asset.width}
              height={asset.height}
            />
            {asset.description && (
              <figcaption className="rich-text__caption">
                {asset.description}
              </figcaption>
            )}
          </figure>
        );
      },

      // Table support
      [BLOCKS.TABLE]: (node, children) => (
        <div className="rich-text__table-wrap">
          <table className="rich-text__table">{children}</table>
        </div>
      ),
      [BLOCKS.TABLE_ROW]: (node, children) => (
        <tr className="rich-text__table-row">{children}</tr>
      ),
      [BLOCKS.TABLE_CELL]: (node, children) => (
        <td className="rich-text__table-cell">{children}</td>
      ),
      [BLOCKS.TABLE_HEADER_CELL]: (node, children) => (
        <th className="rich-text__table-header">{children}</th>
      ),

      // Hyperlinks
      [INLINES.HYPERLINK]: (node, children) => {
        const uri = node.data.uri;
        const isExternal = uri.startsWith('http');

        return (
          <a
            href={uri}
            className="rich-text__link"
            target={isExternal ? '_blank' : undefined}
            rel={isExternal ? 'noopener noreferrer' : undefined}
          >
            {children}
          </a>
        );
      },

      // Entry hyperlinks (internal links)
      [INLINES.ENTRY_HYPERLINK]: (node, children) => {
        const entryId = node.data?.target?.sys?.id;
        const entry = entryMap.get(entryId);

        // If entry not found, render as plain text
        if (!entry) return <span>{children}</span>;

        // Build link based on entry type
        let href = '#';
        if (entry.__typename === 'Article') {
          href = `/articles/${entry.slug}`;
        } else if (entry.__typename === 'CaseStudy') {
          href = `/case-studies/${entry.slug}`;
        }

        return (
          <a href={href} className="rich-text__link">
            {children}
          </a>
        );
      },

      // Asset hyperlinks
      [INLINES.ASSET_HYPERLINK]: (node, children) => {
        const assetId = node.data?.target?.sys?.id;
        const asset = assetMap.get(assetId);

        if (!asset) return <span>{children}</span>;

        return (
          <a
            href={asset.url}
            className="rich-text__link"
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </a>
        );
      },
    },
  };

  if (!content?.json) {
    return null;
  }

  return (
    <div className={`rich-text ${className}`}>
      {documentToReactComponents(content.json, options)}
    </div>
  );
};

export default RichTextRenderer;
